import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { APIURL } from '../../config';
import { Alert, Box, Button, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import MUIRichTextEditor from 'mui-rte';

const readerTheme = createTheme({
    overrides: {
        MUIRichTextEditor: {
            anchorLink: { color: '#1976d2', textDecoration: 'underline' },
            container: { margin: 0, position: 'relative', fontFamily: 'inherit', fontSize: 16 },
            editorContainer: { margin: 0, padding: 0, width: '100%', cursor: 'default' }
        }
    }
});

function releaseDate(value) {
    const date = new Date(value);
    return value && !Number.isNaN(date.getTime())
        ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        : 'Date unavailable';
}

function draftContent(value) {
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed?.blocks) && parsed.entityMap &&
            parsed.blocks.every(block => typeof block.text === 'string')) return value;
    } catch { /* Plain-text notes are also supported. */ }
    return null;
}

export function releaseNotes(description) {
    if (typeof description !== 'string' || !description.trim()) return [];
    const wholeDocument = draftContent(description);
    if (wholeDocument) return [wholeDocument];
    return description.split(/\\n|\r?\n/).filter(line => line.trim()).map((line, index) =>
        draftContent(line) || JSON.stringify({
            blocks: [{ key: `line${index}`, text: line, type: 'unstyled', depth: 0,
                inlineStyleRanges: [], entityRanges: [], data: {} }],
            entityMap: {}
        })
    );
}

export default function WhatsNew() {
    const [versions, setVersions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [retryCount, setRetryCount] = useState(0);
    const readingArea = useRef(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        axios.get(`${APIURL}/system/whatsnew`).then(response => {
            if (!active) return;
            const releases = Array.isArray(response.data) ? [...response.data] : [];
            releases.sort((a, b) => (Date.parse(b.DeploymentDate) || 0) - (Date.parse(a.DeploymentDate) || 0));
            setVersions(releases);
            setCurrentIndex(0);
        }).catch(() => {
            if (active) setError("We couldn't load the updates. Please try again.");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [retryCount]);

    useEffect(() => {
        if (readingArea.current) readingArea.current.scrollTop = 0;
    }, [currentIndex]);

    if (loading) return (
        <Box role="status" aria-label="Loading updates" sx={{ p: { xs: 3, sm: 4 } }}>
            <Skeleton width={100} height={30} />
            <Skeleton width="65%" height={56} sx={{ mb: 3 }} />
            {[90, 100, 80, 95].map(width => <Skeleton key={width} width={`${width}%`} height={30} />)}
        </Box>
    );

    if (error) return (
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setRetryCount(count => count + 1)}>Retry</Button>}>
                {error}
            </Alert>
        </Box>
    );

    if (!versions.length) return (
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ p: 4, flex: 1, minHeight: 300, textAlign: 'center' }}>
            <Box sx={{ display: 'grid', placeItems: 'center', width: 64, height: 64, bgcolor: '#edf7fc', color: '#3FA7D6', borderRadius: 3 }}>
                <NewReleasesOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography variant="h6">More updates on the way</Typography>
            <Typography color="text.secondary">Check back here for the latest improvements to FieldWorker.</Typography>
        </Stack>
    );

    const selected = versions[currentIndex];
    const notes = releaseNotes(selected.Description);

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box component="nav" aria-label="Release history" sx={{
                width: { xs: '100%', md: 240 }, flexShrink: 0, bgcolor: '#f7fafc',
                borderRight: { md: '1px solid #e5edf3' }, borderBottom: { xs: '1px solid #e5edf3', md: 0 },
                p: { xs: 2, md: 2.5 }, overflowY: { md: 'auto' }
            }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 700 }}>
                    Recent releases
                </Typography>
                <Stack spacing={1} direction={{ xs: 'row', md: 'column' }} sx={{ mt: 1, overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 0.5, md: 0 } }}>
                    {versions.map((release, index) => (
                        <Button key={`${release.Version}-${index}`} onClick={() => setCurrentIndex(index)}
                            aria-pressed={currentIndex === index} aria-label={`View version ${release.Version}`}
                            sx={{
                                justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none',
                                flexShrink: 0, minWidth: { xs: 170, md: 0 }, px: 2, py: 1.5, borderRadius: 2,
                                color: currentIndex === index ? '#176489' : 'text.primary',
                                bgcolor: currentIndex === index ? '#e3f2fa' : 'transparent',
                                border: '1px solid', borderColor: currentIndex === index ? '#b8ddec' : 'transparent',
                                '&:hover': { bgcolor: currentIndex === index ? '#d7edf8' : '#edf3f7' }
                            }}>
                            <Stack spacing={0.5} sx={{ width: '100%' }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Typography component="span" fontWeight={700} fontSize={15}>Version {release.Version}</Typography>
                                    {index === 0 && <Chip label="Latest" size="small" sx={{ height: 20, fontSize: 11, bgcolor: '#fff', color: '#176489' }} />}
                                </Stack>
                                <Typography component="span" variant="caption" color="text.secondary">{releaseDate(release.DeploymentDate)}</Typography>
                            </Stack>
                        </Button>
                    ))}
                </Stack>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                <Box ref={readingArea} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 3, sm: 4 }, overflowWrap: 'anywhere' }}>
                    <Box component="article" aria-labelledby="release-title" key={currentIndex}>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Chip label={`Version ${selected.Version}`} size="small" variant="outlined" sx={{ borderColor: '#d7e4ed', color: '#526478' }} />
                            {currentIndex === 0 && <Chip label="Latest release" size="small" sx={{ bgcolor: '#e8f5ee', color: '#276644' }} />}
                        </Stack>
                        <Typography id="release-title" component="h2" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.3, color: '#1f2d3d' }}>
                            {selected.Title || `FieldWorker ${selected.Version}`}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, mb: 3 }}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 15, color: '#7b8b9d' }} />
                            <Typography variant="body2" color="text.secondary">Released {releaseDate(selected.DeploymentDate)}</Typography>
                        </Stack>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{ color: '#354457', fontSize: 16, lineHeight: 1.75,
                            '& .public-DraftStyleDefault-block': { lineHeight: 1.75, margin: '0 0 12px' },
                            '& img': { maxWidth: '100%', height: 'auto' }, '& pre': { maxWidth: '100%', overflowX: 'auto' }
                        }}>
                            {notes.length ? <ThemeProvider theme={readerTheme}>
                                {notes.map((note, index) => <MUIRichTextEditor key={index} defaultValue={note} readOnly toolbar={false} editorStyle={{ textAlign: 'left' }} />)}
                            </ThemeProvider> : <Typography color="text.secondary">Release notes are not available for this update.</Typography>}
                        </Box>
                    </Box>
                </Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid #e5edf3' }}>
                    <Button startIcon={<ArrowBackRoundedIcon />} disabled={currentIndex === 0} onClick={() => setCurrentIndex(index => index - 1)} sx={{ textTransform: 'none' }}>Newer</Button>
                    <Typography variant="caption" color="text.secondary">{currentIndex + 1} of {versions.length}</Typography>
                    <Button endIcon={<ArrowForwardRoundedIcon />} disabled={currentIndex === versions.length - 1} onClick={() => setCurrentIndex(index => index + 1)} sx={{ textTransform: 'none' }}>Older</Button>
                </Stack>
            </Box>
        </Box>
    );
}
